
import { Component, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgStyle } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalendarModule } from 'primeng/calendar';
import { TooltipModule } from 'primeng/tooltip';

import { apps } from '../../../constants';
import { DatePickerLocale } from '../../../models';
import { AppService } from '../../../services';
import { AppStateHolder } from '../../../shared';
import { SearchCalendarComponent } from './calendar-search.component';
import { SearchComponent } from './party-search.component';
import { SearchOwnerComponent } from './search-owner.component';
import { SearchTypologyComponent } from './search-typology.component';
import { UserActivitiesService } from './user-activities.service';


@Component({
    selector: 'calendar-smart-editor',
    templateUrl: 'smart-editor.component.html',
    styleUrls: ['./smart-editor.scss'],
    standalone: true,
    imports: [NgStyle, FormsModule, ReactiveFormsModule, TooltipModule, SearchCalendarComponent, SearchOwnerComponent, SearchTypologyComponent, SearchComponent, CalendarModule, TranslateModule]
})

export class SmartEditorComponent implements OnInit, OnChanges {
  private router = inject(Router);
  appStateHolder = inject(AppStateHolder);
  _appService = inject(AppService);
  private service = inject(UserActivitiesService);
  private translator = inject(TranslateService);
  private toastrService = inject(ToastrService);
  private formBuilder = inject(UntypedFormBuilder);


  @Input() activity: any;
  @Input() activityTimeEditor: boolean;

  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Output() closed: EventEmitter<any> = new EventEmitter();

  smartForm: UntypedFormGroup;

  _date: Date;
  _expireDate: Date;

  locale: any = DatePickerLocale.it;

  calendars: any[];
  //typologies: any[];
  currentSubject: any;

  typologyPrevious: string;
  ownerPrevious: string;
  ownerPreviousFullname: string;
  calendarName: string;
  efTime: string;

  searchingOwners: Boolean = false;
  searchingParties: Boolean = false;

  errorDisplay: boolean = false;
  errorMessage: string;
  searchingCalendar: boolean = false;

  // Dropdown state management for accessibility
  stateDropdownOpen: boolean = false;
  priorityDropdownOpen: boolean = false;

  innerHeight: number;

  activityStatus = [

    ActivityStatus.Todo,
    ActivityStatus.InProgress,
    ActivityStatus.Done
  ];

  @HostListener('window: resize', ['$event'])
  onresize(event) {
    this.innerHeight = window.innerHeight - 203;
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  ngOnInit(): void {

    this.innerHeight = window.innerHeight - 203;

    this.service.getCalendars()
      .subscribe(result => this.calendars = result);

    this._date = new Date(this.activity.date);

    this._expireDate = new Date(this.activity.expiryDate);

    this.efTime = "00:00";

    this.smartForm = this.formBuilder
      .group({
        effectiveTime: [''],
        ownervalid: ['', Validators.required],
        typologyvalid: ['', Validators.required],
        activitysubject: ['', Validators.required],
        calendar: [null, Validators.required],
        estimatedTime: [''],
        priority: ['']
      });

  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes) {
      this._date = new Date(this.activity.date);

      this._expireDate = new Date(this.activity.expiryDate);
    }
  }

  errorClose() {
    this.errorDisplay = false;
    this.errorMessage = '';
  }

  timeChange() {

    if (this.efTime != '' && this.efTime != null && this.efTime.toString() != "00:00") {
      this.activity.effectiveTime = +this.timeToDecimal(this.efTime);
    }
    else
      this.activity.effectiveTime = null;
  }

  timeToDecimal(tim) {
    // let arr = tim.split(':');
    // let dec = parseInt(((+arr[1]/6)*10).toString(), 10);
    // return parseFloat(parseInt(arr[0], 10) + '.' + (dec<10?'0':'') + dec);
    var hoursMinutes = tim.split(':');
    var hours = parseInt(hoursMinutes[0], 10);
    var minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
    return (hours + minutes / 60).toFixed(2);
  }

  decimalToTime(minutes) {

    if (minutes) {
      let sign = minutes < 0 ? "-" : "";
      let min = Math.floor(minutes);
      let sec = Math.round((minutes * 60) % 60);
      return sign + (min < 10 ? "0" : "") + min + ":" + (sec < 10 ? "0" : "") + sec;
    }

    return "00:00";
  }

  save(option: number): void {

    if (this.activity.owner === null || this.activity.activityTypology == null)
      return;

    this.activity.date = this._date.getFullYear() + '-' + (this._date.getMonth() + 1) + '-' + this._date.getDate();

    this.activity.expiryDate = this._expireDate.getFullYear() + '-' + (this._expireDate.getMonth() + 1) + '-' + this._expireDate.getDate();

    if (this.activityTimeEditor) {

      if (this.efTime != '' || this.efTime != null || this.efTime.toString() != "00:00") {
        this.activity.effectiveTime = +this.timeToDecimal(this.efTime);
      }
      else
        this.activity.effectiveTime = null;
    }

    this.service
      .saveActivity(this.activity)
      .then(r => {

        if (option == 1) {
          this.saved.next(1);
        }
        if (option == 2) {
          this.saved.next(2);
        }
        if (option == 3) {
          this.appStateHolder.activityId = this.activity.id;

          this.saved.next(3);
        }

        this.toastrService.success(
          this.translator.instant("app.dataSaved"),
          this.translator.instant("activity-editor.title"));

      }).catch((error: any) => {

        if (error.status == 400) {
          this.errorDisplay = true;
          this.errorMessage = this.translator.instant("app.unexpectedError");
        }
        else {
          this.errorDisplay = true;
          this.errorMessage = error.statusText;
        }

      });
  }

  close(): void {

    this.closed.next(this.activity);
  }

  // parties
  addParty(): void {

    this.searchingParties = !this.searchingParties;
  }
  removeParty(party): void {

    this.activity.parties = this.activity.parties.filter(p => {

      return p !== party;
    });
  }
  onSelectParty(p): void {

    if (p === undefined || p.id === undefined) {

      return;
    }

    this.searchingParties = false;

    let party = {

      idParty: p.id,
      partyName: p.fullName
    };

    this.activity.parties.push(party)
    this.currentSubject = null;
  }
  hasParties(): Boolean {

    if (this.activity.parties === undefined)
      return false;

    return (this.activity.parties.length > 0)
  }
  addRelatedParty(subject: any): void {

    if (this.currentSubject === subject)
      this.currentSubject = undefined;
    else
      this.currentSubject = subject;
  }

  onSelectCalendar(calendar: any) {

    this.searchingCalendar = false;
    this.activity.calendar = calendar;
    this.ownerPrevious = this.activity.owner;
    this.onChangeCalendar();
  }

  removeCalendar() {

    this.searchingCalendar = true;
    this.calendarName = this.activity.calendar.name;
    this.activity.calendar.name = null;

  }

  previousCalendar() {
    this.activity.calendar.name = this.calendarName;
    this.searchingCalendar = false;
  }

  crmLink(partyId: string) {

    if (partyId === undefined)
      return;

    let url = this.appStateHolder.getAppUrl(apps.crm);

    if (url.length === 0) {

      let link = ['/console', partyId];
      this.router.navigate(link)

      return;
    }

    let link = ['/app', `${url}/console/${partyId}`];
    this.router.navigate(link);

  }
  // calendar
  onChangeCalendar(): void {

    this.service
      .getCandidatesOwners(this.activity.calendar.id)
      .subscribe(result => {

        let owner = result.find((own) =>
          own.userName.toUpperCase() === this.ownerPrevious.toUpperCase());

        if (owner === undefined)
          this.removeOwner();
        else {

          this.activity.owner = owner.userName;
        }
        // this.sharedService
        //     .getActivityTypologies()
        //     .subscribe(res => {
        //         let cal = this.calendars.filter(r => {
        //             return r.id == this.activity.calendar.id;
        //         }).map( c => {
        //             return c.idSuiteOwner;
        //         });
        //         this.typologies = res.filter(t =>{
        //             return t.suiteOwnerId == cal[0];
        //         })
        //     if(this.typologies.length == 0)
        //         this.activity.activityTypology.name = null;
        // })
        //this.load = false;
      },
        (error) => {

          if (error == '400 - Bad Request') {
            this.errorDisplay = true;
            this.errorMessage = this.translator.instant("app.unexpectedError");
          }
          else {
            this.errorDisplay = true;
            this.errorMessage = error.statusText;
          }
        });
  }

  // owner
  removeOwner(): void {

    // this.activity.owner  = null;
    this.ownerPrevious = this.activity.owner;
    this.ownerPreviousFullname = this.activity.ownerFullName;

    this.searchingOwners = true;
    //this.activity.owner  = null;
    this.activity.ownerFullName = null;
  }

  previousOwner() {
    this.activity.owner = this.ownerPrevious;
    this.activity.ownerFullName = this.ownerPreviousFullname;
    this.searchingOwners = false;
  }

  onSelectOwner(owner): void {

    this.searchingOwners = false;

    this.activity.owner = owner.userName;
    this.activity.ownerFullName = owner.fullName;
  }

  // typology
  removeTypology(): void {

    this.typologyPrevious = this.activity.activityTypology.name;

    this.activity.activityTypology.name = null;
  }

  previousTypology() {
    this.activity.activityTypology.name = this.typologyPrevious;
  }


  onSelectTypology(typology): void {

    this.activity.activityTypology = typology;
  }

  // datepickers properties
  get date(): Date {

    if (!this._date)
      this._date = new Date(this.activity.date);

    return this._date
  }

  set date(date: Date) {

    this._date = date;
    this._date.setHours(this._date.getHours() - this._date.getTimezoneOffset() / 60);

    this.activity.date = this._date;
  }

  get expiryDate(): Date {

    if (!this._expireDate)
      this._expireDate = new Date(this.activity.expiryDate);

    return this._expireDate
  }

  set expiryDate(date: Date) {

    this._expireDate = date;
    this._expireDate.setHours(this._expireDate.getHours() - this._expireDate.getTimezoneOffset() / 60);

    this.activity.expiryDate = this._expireDate;
  }

  // external links
  openProject(): void {

    let link = ['/project', this.activity.project.id];
    this.router.navigate(link);
  }

  openExtended(): void {

    if (this.activity.isNew)
      return;

    let link = ['/activity', this.activity.id];
    this.router.navigate(link);
  }

  // Dropdown toggle methods for keyboard accessibility
  toggleStateDropdown(): void {
    this.stateDropdownOpen = !this.stateDropdownOpen;
    this.priorityDropdownOpen = false; // Close other dropdown
  }

  togglePriorityDropdown(): void {
    this.priorityDropdownOpen = !this.priorityDropdownOpen;
    this.stateDropdownOpen = false; // Close other dropdown
  }

  handleStateDropdownKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.stateDropdownOpen = false;
    } else if (event.key === 'ArrowDown' && !this.stateDropdownOpen) {
      this.stateDropdownOpen = true;
      event.preventDefault();
    }
  }

  handlePriorityDropdownKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.priorityDropdownOpen = false;
    } else if (event.key === 'ArrowDown' && !this.priorityDropdownOpen) {
      this.priorityDropdownOpen = true;
      event.preventDefault();
    }
  }

  // lookup
  setWorkingState(state: number): void {
    this.activity.workingState = state;
    this.stateDropdownOpen = false; // Close dropdown after selection
  }

  getProjectDescription(): String {

    return this.activity.project.progressive
      .toString() + " - " + this.activity.project.title
  }

  // validation
  isFieldValid(field: string) {

    return !this.smartForm.get(field).valid;
  }

  getControlClass(field: string): String {

    if (this.smartForm.get(field).valid) {

      return "form-control";
    }
    else
      return "form-control form-control-warning";
  }

  setPriorityState(state: number): void {
    this.activity.priority = state;
    this.priorityDropdownOpen = false; // Close dropdown after selection
  }
}


enum ActivityStatus {

  Todo,
  InProgress,
  Done
}
